import React, { useState, useEffect, useMemo } from "react"
import DataTable from "components/Tables/DataTable"
import { Col, Row, Label, FormGroup, Table } from "reactstrap"
import { Link } from "react-router-dom"
import { useFormik } from "formik"
import { getProfiles, putProfiles } from "../../api"

import userStore from "userStore"

import Modal from "components/Modal/Modal"
import Layout from "components/Layout"

import Checkbox from "@mui/material/Checkbox"
import Tooltip from "@mui/material/Tooltip"

// BDSJEA-14: estos objetos NO se resuelven contra esta matriz. El backend los
// tiene fijos en ADMINISTRADOR y no hay tilde que lo cambie.
//
// Si la matriz gobernara la pantalla de permisos o la de usuarios, alcanzaría
// un tilde puesto por error para que un Analista se otorgue lo que quiera. Se
// muestran deshabilitados, con el motivo, en vez de ocultarlos: es mejor que
// se vea que existen y por qué no se tocan.
const OBJETOS_NO_DELEGABLES = {
  "/permisos-por-perfil":
    "Reservado a Administrador: delegarlo permitiría que un perfil se otorgue permisos a sí mismo.",
  "/usuarios":
    "Reservado a Administrador: incluye resetear la clave de cualquier usuario.",
  "/agro/credenciales":
    "Reservado a Administrador: expone las credenciales de los servicios externos.",
  "/auditoria-logs":
    "Reservado a Administrador: es la evidencia de auditoría del sistema.",
}

const MOTIVO_PADRE =
  "Contiene opciones reservadas a Administrador. Si se desactiva, esas pantallas " +
  "desaparecen del menú y no habría forma de volver a habilitarlas desde la aplicación."

// Devuelve el motivo por el que un objeto no se puede tocar, o null.
//
// Se resuelve por ruta y no por nombre: el nombre visible ya cambió una vez
// (V0110) y matchear por ahí deja el bloqueo roto en silencio.
//
// Incluye a los ancestros. Sin eso, destildar "Seguridad" oculta del menú a
// Permisos por Perfil aunque su propio permiso siga intacto.
const construirBloqueos = permisos => {
  const porId = new Map(permisos.map(p => [p.idObjeto, p]))
  const bloqueos = new Map()

  permisos.forEach(p => {
    const motivo = OBJETOS_NO_DELEGABLES[p.route]
    if (!motivo) return
    bloqueos.set(p.idObjeto, motivo)

    let padre = porId.get(Number(p.objParent))
    const visitados = new Set([p.idObjeto])
    while (padre && !visitados.has(padre.idObjeto)) {
      visitados.add(padre.idObjeto)
      if (!bloqueos.has(padre.idObjeto)) {
        bloqueos.set(padre.idObjeto, MOTIVO_PADRE)
      }
      padre = porId.get(Number(padre.objParent))
    }
  })
  return bloqueos
}
import toastHandler from "toastHandler"

import { isEmpty } from "lodash"

const TIPOS_VALIDOS = new Set([0, 1, 100])

const ModalBody = ({ formik, values, setValues, readOnly = false }) => {
  const permisos = (values.permisos || []).filter(
    p => p.objTipo == null || TIPOS_VALIDOS.has(p.objTipo),
  )
  const permisosMenu = permisos.filter(p => p.objTipo !== 100)
  const permisosAdicionales = permisos.filter(p => p.objTipo === 100)

  const bloqueos = useMemo(() => construirBloqueos(permisos), [permisos])
  const motivoBloqueo = permiso => bloqueos.get(permiso.idObjeto) || null

  // Índices de jerarquía (obj_parent -> hijos) para anidar y cascadear.
  const hijosPorPadre = new Map()
  permisosMenu.forEach(p => {
    if (p.objParent != null) {
      const arr = hijosPorPadre.get(p.objParent) || []
      arr.push(p.idObjeto)
      hijosPorPadre.set(p.objParent, arr)
    }
  })
  const porIdObjeto = new Map(permisosMenu.map(p => [p.idObjeto, p]))

  // Profundidad para la tabulación (0 = top-level; 1 = hijo; 2 = nieto...).
  const profundidad = p => {
    let d = 0
    let cur = p
    const visto = new Set()
    while (cur && cur.objParent != null && !visto.has(cur.idObjeto)) {
      visto.add(cur.idObjeto)
      cur = porIdObjeto.get(cur.objParent)
      if (!cur) break
      d++
    }
    return d
  }

  // Todos los idObjeto descendientes (recursivo) de un idObjeto.
  const descendientes = idObjetoRaiz => {
    const result = new Set()
    const stack = [idObjetoRaiz]
    while (stack.length) {
      const cur = stack.pop()
      ;(hijosPorPadre.get(cur) || []).forEach(h => {
        if (!result.has(h)) {
          result.add(h)
          stack.push(h)
        }
      })
    }
    return result
  }

  // Nombre a mostrar: solo la hoja, sin el prefijo del padre.
  const nombreHoja = nombre => (nombre || "").split("->").pop().trim()

  // Reglas (Modificación implica Consulta):
  //  - destildar Consulta => destilda Modificación (no se modifica sin consultar).
  //  - tildar Modificación => tilda Consulta.
  //  - en un padre, tildar/destildar Consulta o Modificación => se replica en sus descendientes.
  //  - si TODOS los hijos quedan tildados (consulta/modificación), se tilda el padre
  //    (solo tilda hacia arriba; nunca destilda al padre, para no quitarle acceso).
  const handleCheck = (idObjeto, field) => {
    if (readOnly) return
    const target = permisos.find(p => p.idObjeto === idObjeto)
    if (!target) return
    const nuevoValor = !target[field]
    // Cascada hacia abajo al destildar (cualquiera) o al tildar Modificación.
    const cascada = !nuevoValor || (field === "modificacion" && nuevoValor)
    const subtree = cascada ? descendientes(idObjeto) : new Set()

    const byId = new Map()
    permisos.forEach(p => {
      const esTarget = p.idObjeto === idObjeto
      const esDescendiente = subtree.has(p.idObjeto)
      if (!esTarget && !esDescendiente) {
        byId.set(p.idObjeto, p)
        return
      }
      // El checkbox deshabilitado no alcanza: destildar un padre cascadea a
      // los descendientes y le sacaría el permiso a un objeto bloqueado sin
      // que nadie lo toque. La cascada tiene que saltearlos.
      if (motivoBloqueo(p)) {
        byId.set(p.idObjeto, p)
        return
      }
      let consulta = p.consulta
      let modificacion = p.modificacion
      if (field === "consulta") {
        if (esTarget) consulta = nuevoValor
        if (!nuevoValor) {
          consulta = false
          modificacion = false
        }
      } else {
        if (esTarget) modificacion = nuevoValor
        if (nuevoValor) {
          consulta = true
          if (esDescendiente) modificacion = true
        } else {
          modificacion = false
        }
      }
      byId.set(p.idObjeto, { ...p, consulta, modificacion })
    })

    // Propagación hacia arriba: si todos los hijos tienen el flag, tildarlo en el padre
    // (de hojas a raíz; solo tilda). Modificación implica Consulta.
    const padres = permisos.filter(p => hijosPorPadre.has(p.idObjeto))
    padres.sort((a, b) => profundidad(b) - profundidad(a))
    padres.forEach(parent => {
      const hijos = (hijosPorPadre.get(parent.idObjeto) || [])
        .map(h => byId.get(h))
        .filter(Boolean)
      if (!hijos.length) return
      const actual = byId.get(parent.idObjeto)
      let consulta = actual.consulta
      let modificacion = actual.modificacion
      if (hijos.every(h => h.modificacion)) {
        modificacion = true
        consulta = true
      }
      if (hijos.every(h => h.consulta)) consulta = true
      if (
        consulta !== actual.consulta ||
        modificacion !== actual.modificacion
      ) {
        byId.set(parent.idObjeto, { ...actual, consulta, modificacion })
      }
    })

    setValues({ ...values, permisos: permisos.map(p => byId.get(p.idObjeto)) })
  }

  return (
    <>
      <Row>
        <Col className="mb-2">
          <FormGroup check inline>
            <Label check>
              <Checkbox
                className="ms-0"
                disabled
                checked={formik.values.admin || false}
              />{" "}
              Administrador
            </Label>
          </FormGroup>
          <FormGroup check inline>
            <Label check>
              <Checkbox
                disabled={readOnly}
                onClick={() =>
                  setValues({
                    ...values,
                    vigente: !formik.values.vigente,
                  })
                }
                checked={formik.values.vigente || false}
              />{" "}
              Vigente
            </Label>
          </FormGroup>
        </Col>
      </Row>

      <div
        className="table-responsive"
        style={{ maxHeight: "55vh", overflowY: "auto" }}
      >
        <Table bordered size="sm" className="mb-0">
          <thead>
            <tr>
              <th
                className="bg-light"
                style={{ width: "60%", position: "sticky", top: 0, zIndex: 1 }}
              >
                Permisos
              </th>
              <th
                className="bg-light text-center"
                style={{ width: "20%", position: "sticky", top: 0, zIndex: 1 }}
              >
                Consulta
              </th>
              <th
                className="bg-light text-center"
                style={{ width: "20%", position: "sticky", top: 0, zIndex: 1 }}
              >
                Modificación
              </th>
            </tr>
          </thead>
          <tbody>
            {permisosMenu.map(p => {
              const nivel = profundidad(p)
              const esPadre = hijosPorPadre.has(p.idObjeto)
              const bloqueado = motivoBloqueo(p)
              const check = campo => {
                const caja = (
                  <Checkbox
                    size="small"
                    disabled={readOnly || !!bloqueado}
                    onClick={() => handleCheck(p.idObjeto, campo)}
                    checked={p[campo] || false}
                  />
                )
                // El span es necesario: MUI no muestra el tooltip sobre un
                // control deshabilitado, porque no emite eventos de mouse.
                return bloqueado ? (
                  <Tooltip title={bloqueado}>
                    <span>{caja}</span>
                  </Tooltip>
                ) : (
                  caja
                )
              }
              return (
                <tr key={p.idObjeto}>
                  <td
                    style={{
                      paddingLeft: 12 + nivel * 24,
                      fontWeight: esPadre ? 600 : 400,
                      color: bloqueado ? "#888" : undefined,
                    }}
                  >
                    {nombreHoja(p.nombreObjeto)}
                    {bloqueado ? (
                      <Tooltip title={bloqueado}>
                        <i className="mdi mdi-lock-outline ms-2 text-muted" />
                      </Tooltip>
                    ) : null}
                  </td>
                  <td className="text-center">{check("consulta")}</td>
                  <td className="text-center">{check("modificacion")}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>

      {permisosAdicionales.length > 0 && (
        <>
          <h6 className="mt-4 mb-2" style={{ fontWeight: 600 }}>
            Permisos adicionales
          </h6>
          <Table bordered size="sm" className="mb-0">
            <thead>
              <tr>
                <th className="bg-light" style={{ width: "60%" }}>
                  Permiso
                </th>
                <th className="bg-light text-center" style={{ width: "20%" }}>
                  Consulta
                </th>
                <th className="bg-light text-center" style={{ width: "20%" }}>
                  Modificación
                </th>
              </tr>
            </thead>
            <tbody>
              {permisosAdicionales.map(p => {
                const bloqueado = motivoBloqueo(p)
                const check = campo => {
                  const caja = (
                    <Checkbox
                      size="small"
                      disabled={readOnly || !!bloqueado}
                      onClick={() => handleCheck(p.idObjeto, campo)}
                      checked={p[campo] || false}
                    />
                  )
                  return bloqueado ? (
                    <Tooltip title={bloqueado}>
                      <span>{caja}</span>
                    </Tooltip>
                  ) : (
                    caja
                  )
                }
                return (
                  <tr key={p.idObjeto}>
                    <td style={{ color: bloqueado ? "#888" : undefined }}>
                      {p.nombreObjeto}
                      {bloqueado ? (
                        <Tooltip title={bloqueado}>
                          <i className="mdi mdi-lock-outline ms-2 text-muted" />
                        </Tooltip>
                      ) : null}
                    </td>
                    <td className="text-center">{check("consulta")}</td>
                    <td className="text-center">{check("modificacion")}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </>
      )}
    </>
  )
}

const ProfileList = ({ permissionBy }) => {
  const [modal, setModal] = useState(false)
  const [values, setValues] = useState({})
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [viewOnly, setViewOnly] = useState(false)

  const setError = toastHandler(state => state.setError)
  const setSuccess = toastHandler(state => state.setSuccess)
  const user = userStore(state => state.user)
  const modificacion = userStore(state => state.modificacion)
  const consulta = userStore(state => state.consulta)

  const columns = [
    {
      name: "Nombre",

      selector: row => (
        <div className="d-flex align-items-center">
          <div className="badge badge-soft-primary font-size-11 m-1">
            {row.descripcion}
          </div>
        </div>
      ),
    },
    {
      name: "Administrador",
      cell: row => (row.admin ? "Sí" : "No"),
    },
    {
      name: "Vigente",
      cell: row => (row.vigente ? "Sí" : "No"),
    },
    ...(modificacion
      ? [
          {
            name: "Editar",
            selector: row => (
              <Link className="text-success" to="#">
                <i
                  className="mdi mdi-pencil font-size-18"
                  id="edittooltip"
                  onClick={() => handleProfileClick(row, false)}
                ></i>
              </Link>
            ),
            grow: 0.1,
            center: true,
          },
        ]
      : consulta
        ? [
            {
              name: "Ver",
              selector: row => (
                <Link className="text-primary" to="#">
                  <i
                    className="mdi mdi-magnify font-size-18"
                    id="viewtooltip"
                    onClick={() => handleProfileClick(row, true)}
                  ></i>
                </Link>
              ),
              grow: 0.1,
              center: true,
            },
          ]
        : []),
  ]

  const customStyles = {
    table: {
      style: {
        padding: "0px 20px",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
        fontWeight: 400,
        fontSize: "13px",
        textOverflow: "clip",
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgb(230, 244, 244)",
        borderBottomColor: "#FFFFFF",
        borderRadius: "25px",
        outline: "1px solid #FFFFFF",
      },
    },
    headCells: {
      style: {
        fontSize: "13px",
        fontWeight: 600,
        color: "rgb(74, 74, 74)",
      },
    },
  }

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      ...values,
    },

    onSubmit: values => {
      values.usuarioModif = user.username
      values.fechaModif = new Date()
      values.permisos = values.permisos.map(perm => ({
        ...perm,
        usuarioModif: user.username,
        fechaModif: new Date(),
      }))
      putProfiles(values)
        .then(() => {
          setSuccess()
          getProfiles()
            .then(profiles => {
              setData(profiles)

              let profile = { admin: false, permisos: [] }
              // Roles del usuario
              const roles = user.roles
              roles.map(role => {
                // context for user permissions
                profiles.filter(item => {
                  if (
                    item.descripcion
                      .toUpperCase()
                      .indexOf(role.replace("ROLE_", "").toUpperCase()) > -1
                  ) {
                    if (isEmpty(profile.permisos)) {
                      profile = {
                        admin: profile.admin || item.admin,
                        permisos: item.permisos,
                      }
                    } else {
                      // Comparar cada item de permisos de profile con los items de permisos de item.permisos y dejar el true
                      profile.permisos.map(perm => {
                        const permiso = item.permisos.find(
                          permItem =>
                            permItem.nombreObjeto == perm.nombreObjeto,
                        )
                        if (!permiso) return
                        perm.consulta = perm.consulta || permiso.consulta
                        perm.modificacion =
                          perm.modificacion || permiso.modificacion
                        perm.habilitaAcceso =
                          perm.habilitaAcceso || permiso.habilitaAcceso
                      })
                    }
                    profile.admin = profile.admin || item.admin
                  }
                })
                userStore.setState({
                  admin: profile.admin,
                  permisos: profile.permisos,
                })
              })
              setLoading(false)
            })
            .catch(err => {
              setError(err)
              setLoading(false)
            })
        })
        .catch(err => {
          setError(err)
          setLoading(false)
        })
      setLoading(true)
      setModal(!modal)
      formik.resetForm()
    },
  })

  const handleProfileClick = (profile, readOnly = false) => {
    setViewOnly(readOnly)
    setValues({ ...values, ...profile })
    toggle()
  }

  const toggle = () => {
    setModal(!modal)
  }

  useEffect(() => {
    if (!modal) {
      setValues({})
      setViewOnly(false)
    }
  }, [modal])

  useEffect(() => {
    getProfiles()
      .then(response => {
        setData(response)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
    setLoading(true)
  }, [])

  return (
    <Layout
      title="Perfiles"
      save={false}
      loading={loading}
      permissionBy={permissionBy}
      components={[
        {
          component: (
            <>
              <DataTable
                columns={columns}
                data={data}
                styles={customStyles}
                dense={false}
                showAll
              />

              <Modal
                open={modal}
                size="lg"
                toggle={toggle}
                readOnly={viewOnly}
                header={
                  viewOnly
                    ? `Permisos del perfil ${formik.values.descripcion}`
                    : `Editar perfil de ${formik.values.descripcion}`
                }
                onSubmit={formik.handleSubmit}
                body={
                  <ModalBody
                    formik={formik}
                    values={values}
                    setValues={setValues}
                    readOnly={viewOnly}
                  />
                }
              />
            </>
          ),
          styles: "p-0",
        },
      ]}
    />
  )
}

export default ProfileList
